import React from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Upload from 'rc-upload';
import Papa from 'papaparse';

import FileDisplay from '../../../common/FileDisplay';

const UploadComponent = ({ step, activeStep, csvFile, setCSVFile, images, setImages }) => {
	const moreThanFiveImages = images.length > 5;
	const [showAllImages, setShowAllImages] = React.useState(false);
	const [csvError, setCsvError] = React.useState('');
	const buttonText = showAllImages ? 'show less' : `+ ${images.length - 5} more...`;

	const toggleShowAllImages = () => setShowAllImages(!showAllImages);

	const validateCsvHeaders = (file) => {
		return new Promise((resolve, reject) => {
			Papa.parse(file, {
				delimiter: '\t',
				quoteChar: '"',
				complete: (result) => {
					if (result.errors.length) {
						reject('Error parsing CSV file.');
					} else {
						const headers = result.data[0].map((header) => header.trim());
						const requiredHeaders = ['file_name', 'latitude[deg]', 'longitude[deg]'];
						const hasAllHeaders = requiredHeaders.every((header) => headers.includes(header));
						if (hasAllHeaders) {
							resolve();
						} else {
							reject(`CSV must include: File Name , Latitude and Longitude.`);
						}
					}
				},
				error: (err) => reject(err.message),
			});
		});
	};

	const csvUploaderProps = {
		accept: '.csv',
		multiple: false,
		beforeUpload: async (file) => {
			try {
				await validateCsvHeaders(file);
				setCsvError('');
				setCSVFile(file);
			} catch (error) {
				setCsvError(error);
			}
			return false;
		},
	};

	const imageUploaderProps = {
		accept: '.jpg,.webp',
		multiple: true,
		beforeUpload: (file) => {
			setImages((currentImages) => [...currentImages, file]);
			return false;
		},
		onRemove(file) {
			const index = images.indexOf(file);
			const newImages = images.slice();
			newImages.splice(index, 1);
			setImages(newImages);
		},
	};

	const isActive = activeStep === step;

	return (
		<>
			<Upload {...(step === 1 ? csvUploaderProps : imageUploaderProps)} disabled={!isActive}>
				<div className={`np__upload-card${isActive ? ' np__upload-card--active' : ' np__upload-card--disabled'}`}>
					<CloudUploadIcon className="np__upload-icon" style={{ fontSize: 40 }} />
					<p className="np__upload-title">
						{step === 1 ? 'Upload CSV file' : 'Upload project images'}
					</p>
					<p className="np__upload-desc">
						{step === 1
							? 'Drag and drop your CSV here, or browse to upload.'
							: 'Drag and drop images here, or browse to upload. Multiple files supported.'}
					</p>
					<button
						className="np__upload-browse-btn"
						type="button"
						disabled={!isActive}
					>
						Browse files
					</button>
				</div>
			</Upload>

			{/* CSV validation error */}
			{csvError && (
				<p className="np__upload-error">{csvError}</p>
			)}

			{/* CSV selected state */}
			{step === 1 && csvFile && (
				<div className="np__selected-file-row">
				<FileDisplay name={csvFile.name} onRemove={() => setCSVFile(null)} isDisabled={!isActive} />
			</div>
		)}

			{/* Images selected state */}
			{step === 2 && images.length > 0 && (
				<>
					<div className="np__image-summary">
						<span className="np__image-count-badge">{images.length}</span>
						<span className="np__image-count-label">
							{images.length === 1 ? 'image selected' : 'images selected'}
						</span>
					</div>
					<div className="np__file-chips">
						{(showAllImages ? images : images.slice(0, 5)).map((image) => (
							<FileDisplay
								key={image.name}
								name={image.name}
								onRemove={() => imageUploaderProps.onRemove(image)}
								isDisabled={!isActive}
							/>
						))}
					</div>
					{moreThanFiveImages && (
						<button className="np__show-more-btn" onClick={toggleShowAllImages} type="button">
							{buttonText}
						</button>
					)}
				</>
			)}
		</>
	);
};

export default UploadComponent;
